import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useParams } from 'react-router';
import { TalkDetailsConsumer } from '../../contexts/TalkDetailsContext';
import { isLiveCheck, isConferenceDayCheck } from '../../utils/helpers';
import { streams, talkTypes } from '../../constants';
import TalkInformation from './components/TalkInformation';
import './TalkDetail.scss';

export const TalkDetail = ({ isLoading, speakers, talks, loadTalkDetails }) => {
  const history = useHistory();
  const { talkId } = useParams();
  const details = talks.find(talk => talk.id === talkId) || {};
  const filteredSpeakers = speakers.filter(speaker => speaker.talks.includes(talkId));

  const [trigger, setTrigger] = useState(new Date());
  const [isConferenceDay, setIsConferenceDay] = useState(false);

  useEffect(() => {
    loadTalkDetails(talkId);
    setIsConferenceDay(isConferenceDayCheck());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleVisibility = () => {
      if (!document.hidden) setTrigger(new Date());
    };
    if (details.track !== 'Interactive 3-hour Hands-on Lab') {
      document.addEventListener('visibilitychange', handleVisibility);
      setIsConferenceDay(isConferenceDayCheck());
      return () => {
        document.removeEventListener('visibilitychange', handleVisibility);
      };
    }
  }, [details.track]);

  useEffect(() => {
    if (details.track !== 'Interactive 3-hour Hands-on Lab') {
      const interval = setInterval(() => {
        setTrigger(new Date());
      }, 1000 * 60);
      return () => clearInterval(interval);
    }
  }, [trigger, details.track]);

  return (
    <div id="talk-details-section">
      <section className="section sp-form" id="talk-details">
        <div className="container sp-container">
          {!isLoading && !talks.some(talk => talk.id === talkId) ? (
            <h4 className="title is-4">Oops ! There is no such talk.</h4>
          ) : (
            <div>
              {isLoading ? (
                <i className="fa fa-spinner fa-spin fa-3x fa-fw" style={{ color: '#7500c0' }} />
              ) : (
                <div>
                  <button
                    className="button back-btn"
                    onClick={() => history.goBack()}
                    type="button">
                    <span className="fa-stack is-large">
                      <i className="fas fa-circle fa-stack-2x" />
                      <i className="fas fa-angle-left fa-lg fa-stack-1x" />
                    </span>
                    <span className="button-text">Back</span>
                  </button>
                  <TalkInformation
                    color={
                      details.track === talkTypes.talk || details.track === talkTypes.lightningTalk
                        ? details.trackInDay
                        : 'workshop'
                    }
                    conferenceDay={details.conferenceDay}
                    endTime={details.endTime}
                    id={details.id}
                    isLive={
                      details.track !== 'Interactive 3-hour Hands-on Lab'
                        ? isLiveCheck(details, trigger)
                        : false
                    }
                    isStream={
                      isConferenceDay &&
                      !details.youtube &&
                      details.track !== 'Interactive 3-hour Hands-on Lab'
                    }
                    liveCoding={details.livecoding || false}
                    proficiency={details.proficiency || ''}
                    room={details.room || ''}
                    speakers={filteredSpeakers}
                    startTime={details.startTime}
                    stream={details.trackInDay ? streams[details.trackInDay].live : ''}
                    summary={details.description || ''}
                    technicalOrBusiness={details.technicalOrBusiness || ''}
                    title={details.title || ''}
                    track={details.track}
                    video={details.youtube || ''}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

TalkDetail.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  loadTalkDetails: PropTypes.func.isRequired,
  speakers: PropTypes.arrayOf(PropTypes.any).isRequired,
  talks: PropTypes.arrayOf(PropTypes.any).isRequired
};

const TalkDetailContainer = props => (
  <TalkDetailsConsumer>
    {({ isLoading, speakers, talks, loadTalkDetails }) => (
      <TalkDetail
        isLoading={isLoading}
        loadTalkDetails={loadTalkDetails}
        speakers={speakers}
        talks={talks}
        {...props}
      />
    )}
  </TalkDetailsConsumer>
);

export default TalkDetailContainer;
